export type DeclarationType = 
  // Impôts directs
  | 'IBP' // Impôt sur les Bénéfices et Profits
  | 'IPR' // Impôt sur le Revenu Professionnel
  | 'IRCM' // Impôt Réel sur le Chiffre d'Affaires
  | 'IRVM' // Impôt sur les Revenus de Valeurs Mobilières
  | 'IPF' // Impôt Professionnel Forfaitaire
  // Taxes indirectes
  | 'TVA' // Taxe sur la Valeur Ajoutée
  | 'TPI' // Taxe pour la Promotion de l'Industrie
  | 'TCR' // Taxe pour la Circulation Routière
  | 'TE' // Taxe Environnementale
  | 'TAD' // Taxe Administrative
  | 'TRD' // Taxe de Rémunération Directe
  // Cotisations sociales
  | 'CNSS' // Cotisations Sociales CNSS
  | 'INPP' // Institut National de Préparation Professionnelle
  | 'ONEM' // Office National de l'Emploi
  // Autres taxes spécifiques
  | 'TSD' // Taxe de Solidarité pour le Développement
  | 'TPU' // Taxe de Publicité
  | 'TSE' // Taxe sur les Spectacles et Événements
  | 'AUTRES'; // Autres taxes non catégorisées

export type DeclarationPeriodicity = 'monthly' | 'quarterly' | 'semi_annual' | 'annual';
export type DeclarationStatus = 'draft' | 'pending' | 'submitted' | 'validated' | 'rejected';

export interface Declaration {
  id: string;
  type: DeclarationType;
  period: string;
  periodicity: DeclarationPeriodicity;
  dueDate: string;
  status: DeclarationStatus;
  amount: number;
  additionalFees?: number; // Frais supplémentaires non justifiés
  penalties?: number; // Pénalités de retard
  submittedAt?: string;
  submittedBy?: string;
  reference?: string;
  attachments?: DeclarationAttachment[];
  justificationDocument?: string; // PDF du justificatif
  declarationForm?: string; // Formulaire de déclaration PDF
  notes?: string;
  validatedAt?: string;
  validatedBy?: string;
}

export interface DeclarationAttachment {
  id: string;
  name: string;
  type: 'justification' | 'declaration_form' | 'supporting_document' | 'receipt';
  url: string;
  uploadedAt: string;
  size: number;
}

export const DECLARATION_TYPES = {
  // Impôts directs
  IBP: {
    label: 'Impôt sur les Bénéfices et Profits',
    periodicity: 'annual' as const,
    dueDate: 31, // 31 Mars de l'année suivante
    category: 'direct_tax',
    description: 'Impôt sur les bénéfices des entreprises (ex-IB)',
    rate: '30%'
  },
  IPR: {
    label: 'Impôt sur le Revenu Professionnel',
    periodicity: 'monthly' as const,
    dueDate: 15, // 15 du mois suivant
    category: 'direct_tax',
    description: 'Impôt retenu à la source sur les revenus professionnels',
    rate: '15%'
  },
  IRCM: {
    label: 'Impôt Réel sur le Chiffre d\'Affaires',
    periodicity: 'monthly' as const,
    dueDate: 15,
    category: 'direct_tax',
    description: 'Impôt sur le chiffre d\'affaires (régime réel)',
    rate: '1%'
  },
  IRVM: {
    label: 'Impôt sur les Revenus de Valeurs Mobilières',
    periodicity: 'monthly' as const,
    dueDate: 15,
    category: 'direct_tax',
    description: 'Impôt sur les dividendes et intérêts',
    rate: '20%'
  },
  IPF: {
    label: 'Impôt Professionnel Forfaitaire',
    periodicity: 'annual' as const,
    dueDate: 31,
    category: 'direct_tax',
    description: 'Impôt forfaitaire pour les petites entreprises',
    rate: 'Forfaitaire'
  },
  
  // Taxes indirectes
  TVA: {
    label: 'Taxe sur la Valeur Ajoutée',
    periodicity: 'monthly' as const,
    dueDate: 15, // 15 du mois suivant
    category: 'indirect_tax',
    description: 'Taxe sur la valeur ajoutée',
    rate: '16%'
  },
  TPI: {
    label: 'Taxe pour la Promotion de l\'Industrie',
    periodicity: 'monthly' as const,
    dueDate: 15,
    category: 'indirect_tax',
    description: 'Taxe de promotion industrielle',
    rate: '1%'
  },
  TCR: {
    label: 'Taxe pour la Circulation Routière',
    periodicity: 'annual' as const,
    dueDate: 31,
    category: 'indirect_tax',
    description: 'Taxe sur les véhicules de transport',
    rate: 'Variable'
  },
  TE: {
    label: 'Taxe Environnementale',
    periodicity: 'quarterly' as const,
    dueDate: 15, // 15 du premier mois du trimestre suivant
    category: 'indirect_tax',
    description: 'Taxe pour la protection de l\'environnement',
    rate: '0.5%'
  },
  TAD: {
    label: 'Taxe Administrative',
    periodicity: 'monthly' as const,
    dueDate: 15,
    category: 'indirect_tax',
    description: 'Taxe sur les actes administratifs',
    rate: 'Variable'
  },
  TRD: {
    label: 'Taxe de Rémunération Directe',
    periodicity: 'monthly' as const,
    dueDate: 15,
    category: 'indirect_tax',
    description: 'Taxe sur les rémunérations',
    rate: '5%'
  },
  
  // Cotisations sociales
  CNSS: {
    label: 'Cotisations Sociales CNSS',
    periodicity: 'monthly' as const,
    dueDate: 10, // 10 du mois suivant
    category: 'social_contribution',
    description: 'Cotisations de sécurité sociale',
    rate: '16.5%'
  },
  INPP: {
    label: 'Institut National de Préparation Professionnelle',
    periodicity: 'monthly' as const,
    dueDate: 15,
    category: 'social_contribution',
    description: 'Cotisation formation professionnelle',
    rate: '3%'
  },
  ONEM: {
    label: 'Office National de l\'Emploi',
    periodicity: 'monthly' as const,
    dueDate: 15,
    category: 'social_contribution',
    description: 'Cotisation pour l\'emploi',
    rate: '1.5%'
  },
  
  // Autres taxes spécifiques
  TSD: {
    label: 'Taxe de Solidarité pour le Développement',
    periodicity: 'quarterly' as const,
    dueDate: 31,
    category: 'special_tax',
    description: 'Taxe de solidarité nationale',
    rate: '1%'
  },
  TPU: {
    label: 'Taxe de Publicité',
    periodicity: 'quarterly' as const,
    dueDate: 15,
    category: 'special_tax',
    description: 'Taxe sur la publicité commerciale',
    rate: 'Variable'
  },
  TSE: {
    label: 'Taxe sur les Spectacles et Événements',
    periodicity: 'quarterly' as const,
    dueDate: 15,
    category: 'special_tax',
    description: 'Taxe sur les spectacles et événements',
    rate: '10%'
  },
  AUTRES: {
    label: 'Autres Taxes',
    periodicity: 'monthly' as const,
    dueDate: 15,
    category: 'other',
    description: 'Autres taxes non catégorisées',
    rate: 'Variable'
  }
} as const;

export const DECLARATION_CATEGORIES = {
  direct_tax: 'Impôts Directs',
  indirect_tax: 'Taxes Indirectes', 
  social_contribution: 'Cotisations Sociales',
  special_tax: 'Taxes Spécifiques',
  other: 'Autres'
} as const;

export const DECLARATION_STATUS_LABELS = {
  draft: 'Brouillon',
  pending: 'À soumettre',
  submitted: 'Soumise',
  validated: 'Validée',
  rejected: 'Rejetée'
} as const;

export const PERIODICITY_LABELS = {
  monthly: 'Mensuelle',
  quarterly: 'Trimestrielle', 
  semi_annual: 'Semestrielle',
  annual: 'Annuelle'
} as const;